import { useState, useEffect, useCallback } from "react"
import { Container, Row, Col, Spinner, Card, Table, Badge, Form, Button, InputGroup } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import API from "../../common/API"
import DateRangeFilter from "../../components/indicators/DateRangeFilter"
import IndicatorCard from "../../components/indicators/IndicatorCard"
import Pagination from "../../components/core/Pagination"
import OrderTracePanel from "../../components/Documents/OrderTracePanel"
import moment from "moment"
import { MerchandiseSearchResult, MerchandiseRecentDispatch, DispatchDetailClient } from "../../types/Indicators.types"

interface Props {
  orgId: string
  initialClientCode?: string
  initialClientName?: string
  readOnlyClient?: boolean
  hideClientFilter?: boolean
}

interface FilterOptions {
  warehouses: string[]
  dependencies: string[]
}

const PAGE_SIZE = 20

export default function MerchandiseSearch({
  orgId,
  initialClientCode = "",
  initialClientName = "",
  readOnlyClient = false,
  hideClientFilter = false,
}: Props) {
  const { t } = useTranslation("global")

  const [term, setTerm] = useState("")
  const [searchedTerm, setSearchedTerm] = useState("")
  const [clientCode, setClientCode] = useState(initialClientCode)
  const [clientName, setClientName] = useState(initialClientName)
  const [clientSearchText, setClientSearchText] = useState("")
  const [clientSuggestions, setClientSuggestions] = useState<Array<{ code: string; name: string }>>([])
  const [isSearchingClient, setIsSearchingClient] = useState(false)
  const [showClientSuggestions, setShowClientSuggestions] = useState(false)

  const [isLoading, setIsLoading] = useState(false)
  const [result, setResult] = useState<MerchandiseSearchResult | null>(null)
  const [startDate, setStartDate] = useState(moment().startOf("month").format("YYYY-MM-DD"))
  const [endDate, setEndDate] = useState(moment().format("YYYY-MM-DD"))

  const [filterOptions, setFilterOptions] = useState<FilterOptions>({ warehouses: [], dependencies: [] })
  const [dependency, setDependency] = useState("")
  const [warehouse, setWarehouse] = useState("")

  const [recent, setRecent] = useState<MerchandiseRecentDispatch[]>([])
  const [isLoadingRecent, setIsLoadingRecent] = useState(false)
  const [topClients, setTopClients] = useState<DispatchDetailClient[]>([])
  const [isLoadingTopClients, setIsLoadingTopClients] = useState(false)
  const [selectedItemCode, setSelectedItemCode] = useState("")
  const [traceOrder, setTraceOrder] = useState("")
  const [page, setPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [totalRecords, setTotalRecords] = useState(0)

  const loadFilterOptions = useCallback(async () => {
    if (!orgId) return
    try {
      const res = await API.getAction("Indicators/MerchandiseFilterOptions", `organizationId=${orgId}`)
      if (res.data.status === "ok") {
        setFilterOptions({
          warehouses: res.data.response?.warehouses ?? [],
          dependencies: res.data.response?.dependencies ?? [],
        })
      }
    } catch (ex) { console.error(ex) }
  }, [orgId])

  useEffect(() => {
    if (initialClientCode) {
      setClientCode(initialClientCode)
      setClientName(initialClientName || initialClientCode)
    }
  }, [initialClientCode, initialClientName])

  const buildFilterParams = (dep: string, wh: string, clCode: string) => {
    let params = ""
    if (dep) params += `&dependency=${encodeURIComponent(dep)}`
    if (wh) params += `&warehouse=${encodeURIComponent(wh)}`
    if (clCode) params += `&clientCode=${encodeURIComponent(clCode)}`
    return params
  }

  const loadRecent = async (
    searchTerm: string,
    itemCode: string,
    newPage: number,
    start: string,
    end: string,
    dep: string,
    wh: string,
    clCode: string
  ) => {
    if (!orgId) return
    try {
      setIsLoadingRecent(true)
      let params = `organizationId=${orgId}&startDate=${start}&endDate=${end}&page=${newPage}&quantityPerPage=${PAGE_SIZE}`
      if (itemCode) {
        params += `&itemCode=${encodeURIComponent(itemCode)}`
      } else if (searchTerm && searchTerm !== "(Todos)") {
        params += `&term=${encodeURIComponent(searchTerm)}`
      }
      params += buildFilterParams(dep, wh, clCode)
      const res = await API.getAction("Indicators/MerchandiseRecentDispatches", params)
      if (res.data.status === "ok") {
        setRecent(res.data.response ?? [])
        setTotalPages(res.data.quantityOfPages ?? 0)
        setTotalRecords(res.data.quantityOfRecords ?? 0)
        setPage(newPage)
      }
    } catch (ex) { console.error(ex) }
    finally { setIsLoadingRecent(false) }
  }

  const loadTopClients = async (
    searchTerm: string,
    itemCode: string,
    start: string,
    end: string,
    dep: string,
    wh: string,
    clCode: string
  ) => {
    if (!orgId) return
    try {
      setIsLoadingTopClients(true)
      let params = `organizationId=${orgId}&startDate=${start}&endDate=${end}`
      if (itemCode) {
        params += `&itemCode=${encodeURIComponent(itemCode)}`
      } else if (searchTerm && searchTerm !== "(Todos)") {
        params += `&term=${encodeURIComponent(searchTerm)}`
      }
      params += buildFilterParams(dep, wh, clCode)
      const res = await API.getAction("Indicators/MerchandiseTopClients", params)
      if (res.data.status === "ok") setTopClients(res.data.response ?? [])
    } catch (ex) { console.error(ex) }
    finally { setIsLoadingTopClients(false) }
  }

  const search = async (
    start = startDate,
    end = endDate,
    dep = dependency,
    wh = warehouse,
    clCode = clientCode,
    forcedTerm?: string
  ) => {
    if (!orgId) return
    const activeTerm = forcedTerm !== undefined ? forcedTerm : term
    const trimmed = activeTerm.trim()
    if (!trimmed && !clCode) return

    try {
      setIsLoading(true)
      setStartDate(start)
      setEndDate(end)
      setSearchedTerm(trimmed || "(Todos)")
      setSelectedItemCode("")
      let params = `organizationId=${orgId}&startDate=${start}&endDate=${end}`
      if (trimmed) params += `&term=${encodeURIComponent(trimmed)}`
      params += buildFilterParams(dep, wh, clCode)
      const res = await API.getAction("Indicators/MerchandiseSearch", params)
      if (res.data.status === "ok") setResult(res.data.response ?? null)
      await Promise.all([
        loadRecent(trimmed, "", 0, start, end, dep, wh, clCode),
        loadTopClients(trimmed, "", start, end, dep, wh, clCode),
      ])
    } catch (ex) { console.error(ex) }
    finally { setIsLoading(false) }
  }

  const handleClientSearchChange = async (val: string) => {
    setClientSearchText(val)
    if (!val || val.trim().length < 2) {
      setClientSuggestions([])
      setShowClientSuggestions(false)
      return
    }
    try {
      setIsSearchingClient(true)
      const res = await API.getAction("Customer/SearchErpClients", `OrganizationId=${orgId}&Search=${encodeURIComponent(val.trim())}`)
      if (res.data?.status === "ok") {
        const items = (res.data.response || []).map((x: { code?: string; clientCode?: string; name?: string; clientName?: string }) => ({
          code: x.code || x.clientCode || "",
          name: x.name || x.clientName || ""
        }))
        setClientSuggestions(items)
        setShowClientSuggestions(true)
      }
    } catch (e) {
      console.error(e)
    } finally {
      setIsSearchingClient(false)
    }
  }

  const selectClient = (c: { code: string; name: string }) => {
    setClientCode(c.code)
    setClientName(c.name)
    setClientSearchText("")
    setClientSuggestions([])
    setShowClientSuggestions(false)
    search(startDate, endDate, dependency, warehouse, c.code)
  }

  const clearClient = () => {
    setClientCode("")
    setClientName("")
    setClientSearchText("")
    setClientSuggestions([])
    setShowClientSuggestions(false)
    if (term.trim()) {
      search(startDate, endDate, dependency, warehouse, "")
    } else {
      setResult(null)
      setRecent([])
      setTopClients([])
      setSearchedTerm("")
    }
  }

  useEffect(() => {
    setResult(null)
    setRecent([])
    setSearchedTerm("")
    setSelectedItemCode("")
    setDependency("")
    setWarehouse("")
    loadFilterOptions()

    if (initialClientCode) {
      search(startDate, endDate, "", "", initialClientCode)
    }
  }, [orgId, initialClientCode, loadFilterOptions])

  const changeDependency = (value: string) => {
    setDependency(value)
    if (searchedTerm || clientCode) search(startDate, endDate, value, warehouse, clientCode)
  }

  const changeWarehouse = (value: string) => {
    setWarehouse(value)
    if (searchedTerm || clientCode) search(startDate, endDate, dependency, value, clientCode)
  }

  const filterByProduct = (itemCode: string) => {
    const next = selectedItemCode === itemCode ? "" : itemCode
    setSelectedItemCode(next)
    loadRecent(searchedTerm, next, 0, startDate, endDate, dependency, warehouse, clientCode)
    loadTopClients(searchedTerm, next, startDate, endDate, dependency, warehouse, clientCode)
  }

  const monthLabel = (m: { year: number; month: number }) =>
    moment(`${m.year}-${String(m.month).padStart(2, "0")}-01`).format("MMM YYYY")

  const hasActiveFilter = !!(searchedTerm || clientCode)

  return (
    <Container fluid className="px-0 py-3">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h5 className="mb-0">
          <i className="fas fa-magnifying-glass-chart me-2 text-primary" />
          {t("Concept Search")}
          {clientCode && (
            <Badge bg="primary" className="ms-2 fw-normal">
              {clientName || clientCode}
            </Badge>
          )}
        </h5>
      </div>

      <Card className="shadow-sm border-0 mb-4">
        <Card.Body>
          <DateRangeFilter
            onFilter={(s, e) => {
              if (searchedTerm || clientCode) {
                search(s, e, dependency, warehouse, clientCode)
              } else {
                setStartDate(s)
                setEndDate(e)
              }
            }}
            loading={isLoading}
          />

          <Row className="g-2 align-items-end">
            <Col md={hideClientFilter ? 5 : 3} lg={hideClientFilter ? 4 : 3}>
              <Form.Label className="small text-muted mb-1">{t("Concepto / Material")}</Form.Label>
              <InputGroup size="sm">
                <InputGroup.Text><i className="fas fa-search" /></InputGroup.Text>
                <Form.Control
                  placeholder={t("Ej. varilla, cemento, bloques...")}
                  value={term}
                  onChange={e => setTerm(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") search() }}
                />
              </InputGroup>
            </Col>

            {!hideClientFilter && (
              <Col md={4} lg={3}>
                <Form.Label className="small text-muted mb-1">{t("Cliente")}</Form.Label>
                {clientCode ? (
                  <div
                    className="d-flex align-items-center justify-content-between p-1 px-2 border rounded bg-light"
                    style={{ minHeight: "31px" }}
                  >
                    <div className="text-truncate me-2" title={`${clientName} (${clientCode})`}>
                      <i className="fas fa-user-check text-success me-1" />
                      <span className="fw-semibold small">{clientName || clientCode}</span>
                      {clientName && clientName !== clientCode && (
                        <small className="text-muted ms-1">({clientCode})</small>
                      )}
                    </div>
                    {!readOnlyClient && (
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0 text-muted"
                        onClick={clearClient}
                        title={t("Quitar filtro de cliente")}
                      >
                        <i className="fas fa-times-circle" />
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="position-relative">
                    <InputGroup size="sm">
                      <InputGroup.Text><i className="fas fa-user" /></InputGroup.Text>
                      <Form.Control
                        placeholder={t("Buscar cliente (nombre o código)...")}
                        value={clientSearchText}
                        onChange={e => handleClientSearchChange(e.target.value)}
                        onFocus={() => { if (clientSuggestions.length > 0) setShowClientSuggestions(true) }}
                      />
                      {isSearchingClient && (
                        <InputGroup.Text><Spinner animation="border" size="sm" /></InputGroup.Text>
                      )}
                    </InputGroup>
                    {showClientSuggestions && clientSuggestions.length > 0 && (
                      <div
                        className="position-absolute bg-white border rounded shadow-sm w-100 mt-1"
                        style={{ zIndex: 1050, maxHeight: "220px", overflowY: "auto" }}
                      >
                        {clientSuggestions.map(c => (
                          <div
                            key={c.code}
                            className="p-2 border-bottom hover-bg cursor-pointer small"
                            style={{ cursor: "pointer" }}
                            onClick={() => selectClient(c)}
                          >
                            <div className="fw-semibold text-truncate">{c.name}</div>
                            <small className="text-muted">{c.code}</small>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </Col>
            )}

            <Col md={hideClientFilter ? 3 : 2} lg={2}>
              <Form.Label className="small text-muted mb-1">{t("Dependency / Zone")}</Form.Label>
              <Form.Select size="sm" value={dependency} onChange={e => changeDependency(e.target.value)}>
                <option value="">{t("All dependencies")}</option>
                {filterOptions.dependencies.map(d => <option key={d} value={d}>{d}</option>)}
              </Form.Select>
            </Col>

            <Col md={hideClientFilter ? 3 : 2} lg={2}>
              <Form.Label className="small text-muted mb-1">{t("Origin Warehouse")}</Form.Label>
              <Form.Select size="sm" value={warehouse} onChange={e => changeWarehouse(e.target.value)}>
                <option value="">{t("All warehouses")}</option>
                {filterOptions.warehouses.map(w => <option key={w} value={w}>{w}</option>)}
              </Form.Select>
            </Col>

            <Col md={hideClientFilter ? 1 : 2} lg={2}>
              <Button
                variant="primary"
                size="sm"
                className="w-100"
                onClick={() => search()}
                disabled={isLoading || (!term.trim() && !clientCode) || !orgId}
              >
                {isLoading ? <i className="fas fa-spinner fa-spin me-1" /> : <i className="fas fa-search me-1" />}
                {t("Search")}
              </Button>
            </Col>
          </Row>

          {/* Quick concept suggestion chips */}
          <div className="d-flex align-items-center flex-wrap gap-2 mt-3 pt-2 border-top">
            <small className="text-muted fw-semibold">{t("Conceptos frecuentes:")}</small>
            {["Varilla", "Cemento", "Bloques", "Acero", "Pintura", "Arena"].map(c => (
              <Badge
                key={c}
                bg={term.toLowerCase() === c.toLowerCase() ? "primary" : "light"}
                text={term.toLowerCase() === c.toLowerCase() ? "white" : "dark"}
                className="cursor-pointer border py-1 px-2"
                style={{ cursor: "pointer" }}
                onClick={() => {
                  setTerm(c)
                  search(startDate, endDate, dependency, warehouse, clientCode, c)
                }}
              >
                {c}
              </Badge>
            ))}
            {term && (
              <Badge
                bg="light"
                text="muted"
                className="cursor-pointer border py-1 px-2 ms-auto"
                style={{ cursor: "pointer" }}
                onClick={() => {
                  setTerm("")
                  if (clientCode) search(startDate, endDate, dependency, warehouse, clientCode, "")
                }}
              >
                <i className="fas fa-xmark me-1" /> {t("Limpiar concepto")}
              </Badge>
            )}
          </div>
        </Card.Body>
      </Card>

      {!orgId ? (
        <Card className="border-0 shadow-sm">
          <Card.Body className="py-4 text-center text-muted">
            {t("Select an organization to load dispatch indicators")}
          </Card.Body>
        </Card>
      ) : !hasActiveFilter ? (
        <Card className="border-0 shadow-sm">
          <Card.Body className="py-5 text-center text-muted">
            <i className="fas fa-magnifying-glass-chart fa-3x mb-3 text-secondary" />
            <h6>{t("Consulta de Movimientos por Artículo o Cliente")}</h6>
            <p className="small text-muted mb-0">
              {t("Escriba un concepto como 'varilla' o seleccione un cliente para analizar su historial mensual de despachos y cotizaciones.")}
            </p>
          </Card.Body>
        </Card>
      ) : isLoading ? (
        <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
      ) : result && (
        <>
          <Row className="g-3 mb-4">
            <Col sm={6} lg={clientCode ? 3 : 2}>
              <IndicatorCard title={t("Total Quantity Moved")} value={Math.round(result.summary.totalQuantity)} icon="fa-boxes-stacked" />
            </Col>
            <Col sm={6} lg={clientCode ? 2 : 2}>
              <IndicatorCard title={t("Dispatches")} value={result.summary.dispatchCount} icon="fa-truck" />
            </Col>
            <Col sm={6} lg={clientCode ? 2 : 2}>
              <IndicatorCard title={t("Products")} value={result.summary.distinctProducts} icon="fa-box" />
            </Col>
            {clientCode ? (
              <>
                <Col sm={6} lg={2}>
                  <IndicatorCard
                    title={t("Cotizaciones")}
                    value={result.summary.quotesCount ?? 0}
                    icon="fa-file-invoice"
                  />
                </Col>
                <Col sm={6} lg={3}>
                  <IndicatorCard
                    title={t("Monto Cotizado")}
                    value={`$${(result.summary.quotedAmount ?? 0).toLocaleString()}`}
                    icon="fa-dollar-sign"
                  />
                </Col>
              </>
            ) : (
              <>
                <Col sm={6} lg={3}>
                  <IndicatorCard title={t("Clients")} value={result.summary.distinctClients} icon="fa-users" />
                </Col>
                <Col sm={6} lg={3}>
                  <IndicatorCard title={t("Warehouses")} value={result.summary.distinctWarehouses} icon="fa-warehouse" />
                </Col>
              </>
            )}
          </Row>

          <Row className="g-3 mb-4">
            <Col lg={6}>
              <Card className="shadow-sm border-0 h-100">
                <Card.Header className="bg-white fw-semibold d-flex justify-content-between align-items-center">
                  <span>
                    <i className="fas fa-ranking-star me-2 text-primary" />
                    {t("Top Products")}
                  </span>
                  {selectedItemCode && (
                    <Button variant="link" size="sm" className="p-0 text-muted" onClick={() => filterByProduct(selectedItemCode)}>
                      {t("Mostrar todos")}
                    </Button>
                  )}
                </Card.Header>
                <Card.Body className="p-0" style={{ maxHeight: 360, overflowY: "auto" }}>
                  <Table hover responsive className="mb-0">
                    <thead className="table-light">
                      <tr>
                        <th className="ps-3">{t("Description")}</th>
                        <th>{t("Brand")}</th>
                        <th className="text-end">{t("Total Qty")}</th>
                        <th className="text-end pe-3">{t("Dispatches")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.topProducts.length === 0 && (
                        <tr><td colSpan={4} className="text-center text-muted py-4">{t("No data available")}</td></tr>
                      )}
                      {result.topProducts.map((p, i) => (
                        <tr
                          key={i}
                          role="button"
                          className={selectedItemCode === p.itemCode ? "table-active" : ""}
                          onClick={() => filterByProduct(p.itemCode)}
                          title={t("Click to see recent dispatches of this product")}
                        >
                          <td className="ps-3">
                            <div className="fw-semibold">{p.description}</div>
                            <small className="text-muted">{p.itemCode}</small>
                          </td>
                          <td>{p.brand}</td>
                          <td className="text-end">{p.totalQuantity.toLocaleString()}</td>
                          <td className="text-end pe-3"><Badge bg="secondary">{p.dispatchCount}</Badge></td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={clientCode ? 6 : 3}>
              <Card className="shadow-sm border-0 h-100">
                <Card.Header className="bg-white fw-semibold d-flex justify-content-between align-items-center">
                  <span>
                    <i className="fas fa-chart-line me-2 text-primary" />
                    {t("Monthly Trend")}
                  </span>
                  {clientCode && (
                    <Badge bg="info" className="fw-normal">
                      {t("Despachos y Cotizaciones")}
                    </Badge>
                  )}
                </Card.Header>
                <Card.Body className="p-0" style={{ maxHeight: 360, overflowY: "auto" }}>
                  <Table hover responsive className="mb-0">
                    <thead className="table-light">
                      <tr>
                        <th className="ps-3">{t("Mes")}</th>
                        <th className="text-end">{t("Cant. Desp.")}</th>
                        <th className="text-end">{t("Desp.")}</th>
                        {clientCode && (
                          <>
                            <th className="text-end">{t("Cotiz.")}</th>
                            <th className="text-end pe-3">{t("Monto Cotiz.")}</th>
                          </>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {result.monthlyTrend.length === 0 && (
                        <tr><td colSpan={clientCode ? 5 : 3} className="text-center text-muted py-4">{t("No data available")}</td></tr>
                      )}
                      {result.monthlyTrend.map((m, i) => (
                        <tr key={i}>
                          <td className="ps-3 text-capitalize fw-semibold">{monthLabel(m)}</td>
                          <td className="text-end fw-semibold text-primary">{m.totalQuantity.toLocaleString()}</td>
                          <td className="text-end"><Badge bg="secondary">{m.dispatchCount}</Badge></td>
                          {clientCode && (
                            <>
                              <td className="text-end">
                                {m.quotesCount ? <Badge bg="info">{m.quotesCount}</Badge> : "—"}
                              </td>
                              <td className="text-end pe-3 small">
                                {m.quotedAmount ? `$${m.quotedAmount.toLocaleString()}` : "—"}
                              </td>
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>

            {!clientCode && (
              <Col lg={3}>
                <Card className="shadow-sm border-0 h-100">
                  <Card.Header className="bg-white fw-semibold">
                    <i className="fas fa-warehouse me-2 text-primary" />
                    {t("By Warehouse")}
                  </Card.Header>
                  <Card.Body className="p-0" style={{ maxHeight: 360, overflowY: "auto" }}>
                    <Table hover responsive className="mb-0">
                      <tbody>
                        {result.byWarehouse.map((w, i) => (
                          <tr key={i}>
                            <td className="ps-3">{w.warehouse}</td>
                            <td className="text-end pe-3">{w.totalQuantity.toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>
            )}
          </Row>

          <Row className="g-3 mb-4">
            {!clientCode && (
              <Col lg={6}>
                <Card className="shadow-sm border-0">
                  <Card.Header className="bg-white fw-semibold d-flex align-items-center justify-content-between">
                    <span>
                      <i className="fas fa-location-dot me-2 text-primary" />
                      {t("Top Destinations")}
                      {selectedItemCode && (
                        <Badge bg="info" className="ms-2">{selectedItemCode}</Badge>
                      )}
                    </span>
                    {isLoadingTopClients && <Spinner animation="border" size="sm" variant="primary" />}
                  </Card.Header>
                  <Card.Body className="p-0" style={{ maxHeight: 400, overflowY: "auto" }}>
                    <Table hover responsive className="mb-0">
                      <thead className="table-light">
                        <tr>
                          <th className="ps-3">#</th>
                          <th>{t("Client")}</th>
                          <th className="text-end">{t("Total Qty")}</th>
                          <th className="text-end pe-3">{t("Dispatches")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topClients.length === 0 && !isLoadingTopClients && (
                          <tr><td colSpan={4} className="text-center text-muted py-4">{t("No data available")}</td></tr>
                        )}
                        {topClients.map((c, i) => (
                          <tr
                            key={i}
                            role="button"
                            style={{ cursor: "pointer" }}
                            onClick={() => selectClient({ code: c.clientCode, name: c.clientName })}
                            title={t("Haga clic para filtrar exclusivamente por este cliente")}
                          >
                            <td className="ps-3 text-muted">{i + 1}</td>
                            <td>
                              <div className="fw-semibold text-primary">{c.clientName}</div>
                              <small className="text-muted">{c.clientCode}</small>
                            </td>
                            <td className="text-end">{c.totalQuantity.toLocaleString()}</td>
                            <td className="text-end pe-3"><Badge bg="secondary">{c.dispatchCount}</Badge></td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>
            )}

            {clientCode && result.byWarehouse.length > 0 && (
              <Col lg={6}>
                <Card className="shadow-sm border-0">
                  <Card.Header className="bg-white fw-semibold">
                    <i className="fas fa-warehouse me-2 text-primary" />
                    {t("Distribución por Almacén de Despacho")}
                  </Card.Header>
                  <Card.Body className="p-0" style={{ maxHeight: 400, overflowY: "auto" }}>
                    <Table hover responsive className="mb-0">
                      <thead className="table-light">
                        <tr>
                          <th className="ps-3">{t("Almacén")}</th>
                          <th className="text-end">{t("Total Cantidad")}</th>
                          <th className="text-end pe-3">{t("Despachos")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.byWarehouse.map((w, i) => (
                          <tr key={i}>
                            <td className="ps-3 fw-semibold">{w.warehouse}</td>
                            <td className="text-end">{w.totalQuantity.toLocaleString()}</td>
                            <td className="text-end pe-3"><Badge bg="secondary">{w.dispatchCount}</Badge></td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>
            )}
          </Row>

          <Card className="shadow-sm border-0">
            <Card.Header className="bg-white fw-semibold d-flex align-items-center justify-content-between">
              <span>
                <i className="fas fa-clock-rotate-left me-2 text-primary" />
                {t("Recent Dispatches")}
                {selectedItemCode && (
                  <Badge bg="info" className="ms-2" role="button" onClick={() => filterByProduct(selectedItemCode)}>
                    {selectedItemCode} <i className="fas fa-xmark ms-1" />
                  </Badge>
                )}
                {clientCode && (
                  <Badge bg="secondary" className="ms-2">
                    {clientCode}
                  </Badge>
                )}
              </span>
              {isLoadingRecent && <Spinner animation="border" size="sm" variant="primary" />}
            </Card.Header>
            <Card.Body className="p-0">
              <Table hover responsive className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th className="ps-3">{t("Date")}</th>
                    <th>{t("Description")}</th>
                    <th className="text-end">{t("Quantity")}</th>
                    <th>{t("Unit")}</th>
                    <th>{t("Warehouse")}</th>
                    <th>{t("Client")}</th>
                    <th>{t("Seller")}</th>
                    <th>{t("Conduce")}</th>
                    <th className="pe-3">{t("Invoice")}</th>
                  </tr>
                </thead>
                <tbody>
                  {recent.length === 0 && !isLoadingRecent && (
                    <tr><td colSpan={9} className="text-center text-muted py-4">{t("No data available")}</td></tr>
                  )}
                  {recent.map((r, i) => (
                    <tr key={i}>
                      <td className="ps-3 text-nowrap">{moment(r.date).format("DD/MM/YYYY")}</td>
                      <td>
                        <div className="fw-semibold">{r.description}</div>
                        <small className="text-muted">{r.itemCode}</small>
                      </td>
                      <td className="text-end fw-semibold text-primary">{r.quantity.toLocaleString()}</td>
                      <td>{r.unit}</td>
                      <td>{r.warehouse}</td>
                      <td>{r.clientName}</td>
                      <td>{r.sellerName}</td>
                      <td><small>{r.conduceNumber}</small></td>
                      <td className="pe-3">
                        {r.invoiceNumber ? (
                          <button
                            className="btn btn-link btn-sm p-0 text-decoration-none"
                            title={t("Ver documentos relacionados")}
                            onClick={() => setTraceOrder(r.invoiceNumber)}
                          >
                            {r.invoiceNumber}
                          </button>
                        ) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
            {totalPages > 1 && (
              <Card.Footer className="bg-white">
                <Pagination
                  totalPages={totalPages}
                  currentPage={page}
                  totalRecords={totalRecords}
                  onPageChanged={(p: number) =>
                    loadRecent(searchedTerm, selectedItemCode, p, startDate, endDate, dependency, warehouse, clientCode)
                  }
                />
              </Card.Footer>
            )}
          </Card>
        </>
      )}

      <OrderTracePanel
        show={!!traceOrder}
        orderNumber={traceOrder}
        organizationId={orgId}
        onHide={() => setTraceOrder("")}
      />
    </Container>
  )
}

